<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import type { ProjectEditorViewModel } from '@Projects/Components/ProjectEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: ProjectEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'update-project', projectId: number | string | null, name: string | null, description: string | null): void
        (e: 'delete-project', projectId: number | string | null): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const updating = computed(() => vm.value.updating)
    const deleting = computed(() => vm.value.deleting)

    const nameError = computed(() => vm.value.nameError)
    const descriptionError = computed(() => vm.value.descriptionError)

    const name = computed<string | null>({
        get: () => vm.value.name,
        set: val => { vm.value.name = val }
    })

    const description = computed<string | null>({
        get: () => vm.value.description,
        set: val => { vm.value.description = val }
    })

    // ---------------- methods ----------------

    function handleFormSubmit()
    {
        vm.value.updating = true
        emit('update-project', vm.value.projectId, name.value, description.value)
    }

    function handleDeleteProject()
    {
        vm.value.deleting = true
        emit('delete-project', vm.value.projectId)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Update</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="project-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>* Name (2-100 characters):</label>
                                <input v-model.trim="name" type="text" class="form-control" required minlength="2" maxlength="100">
                                <div v-if="nameError" class="error">{{nameError}}</div>
                            </div>
                            <div class="form-group">
                                <label>Description (optional):</label>
                                <textarea v-model.trim="description" class="form-control"></textarea>
                                <div class="error">{{descriptionError}}</div>
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer justify-content-between">
                        <div class="dropdown dropup">
                            <button :disabled="deleting || updating" type="button" class="btn btn-danger float-left" data-toggle="dropdown">{{ deleting ? 'Deleting...' : 'Delete' }}</button>
                            <div class="dropdown-menu" role="menu" style="padding: 10px;">
                                <a class="dropdown-item">Cancel</a>
                                <a class="dropdown-item" @click="handleDeleteProject">Confirm</a>
                            </div>
                        </div>
                        <div>
                            <button type="button" @click="close" :disabled="deleting || updating" class="btn btn-secondary mr-1">Cancel</button>
                            <button type="submit" form="project-editor-form" :disabled="deleting || updating" class="btn btn-primary">
                                {{updating ? 'Saving...' : 'Save'}}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

